/* Shopify‑scoped Calculator (supports multiple section instances) */
(() => {
  'use strict';

  // ----- Tunables -----
  const DEFAULT_STATE = {
    disposableCost: 0.50,
    usageFrequency: 1,
    usagePeriod: 'daily', // 'daily' or 'weekly'
    reusableCost: 25.00
  };
  const LIFESPAN_MONTHS = 36; // Fen X custom earplugs lifespan

  // Helpers
  const num = (v, fallback = 0) => {
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  };

  // Expose a scoped initializer that Step B1 will call
  window.initCalculator = function initCalculator(rootId) {
    const root = document.getElementById(rootId);
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    // ----- Instance state (per section) -----
    const state = { ...DEFAULT_STATE };
    const savings = { breakeven: 0, yearly: 0, lifetime: 0, fiveYear: 0 }; // fiveYear kept for back‑compat

    // Scoped query helpers
    const $  = (sel) => root.querySelector(sel);
    const $$ = (sel) => Array.from(root.querySelectorAll(sel));

    // DOM elements (scoped under this section)
    const elements = {
      usageFrequency: $('#usageFrequency'),
      usagePeriod:    $('#usagePeriod'),
      disposableCost: $('#disposableCost'),
      reusableCost:   $('#reusableCost'),
      breakEvenValue: $('#breakEvenValue'),
      yearlyValue:    $('#yearlyValue'),
      lifetimeValue:  $('#lifetimeValue'),
      shareSection:   $('#shareSection'),
      copyBtn:        $('#copyBtn'),
      copyFeedback:   $('#copyFeedback'),
      costChart:      $('#costChart')
    };

    // Read initial values from the inputs (if present)
    if (elements.usageFrequency?.value) state.usageFrequency = num(elements.usageFrequency.value, 1);
    if (elements.usagePeriod?.value)    state.usagePeriod    = elements.usagePeriod.value;
    if (elements.disposableCost?.value) state.disposableCost = num(elements.disposableCost.value, 0.5);
    if (elements.reusableCost?.value)   state.reusableCost   = num(elements.reusableCost.value, 25);

    // Wire events, run once
    setupEventListeners();
    calculateSavings();
    setupTabs();

    // ===== Functions (scoped to this instance) =====
    function setupEventListeners() {
      elements.usageFrequency?.addEventListener('input', handleUsageFrequencyChange);
      elements.usagePeriod?.addEventListener('change', handleUsagePeriodChange);
      elements.disposableCost?.addEventListener('input', handleDisposableCostChange);
      elements.reusableCost?.addEventListener('input', handleReusableCostChange);

      // Share + copy (scoped)
      $$('.share-btn[data-platform]').forEach(btn => btn.addEventListener('click', handleShare));
      elements.copyBtn?.addEventListener('click', handleCopyToClipboard);
    }

    function handleUsageFrequencyChange(e) {
      const v = e.target.value;
      state.usageFrequency = v === '' ? 0 : Math.max(0, parseInt(v, 10) || 0);
      calculateSavings();
    }
    function handleUsagePeriodChange(e) {
      state.usagePeriod = e.target.value;
      calculateSavings();
    }
    function handleDisposableCostChange(e) {
      state.disposableCost = num(e.target.value, 0);
      calculateSavings();
    }
    function handleReusableCostChange(e) {
      state.reusableCost = num(e.target.value, 0);
      calculateSavings();
    }

    function calculateSavings() {
      const { disposableCost, usageFrequency, usagePeriod, reusableCost } = state;

      // Breakeven in periods (days or weeks)
      const costPerUsagePeriod = usageFrequency * disposableCost;
      const breakeven = costPerUsagePeriod > 0 ? reusableCost / costPerUsagePeriod : 0;

      // Daily usage basis for monthly/yearly math
      const dailyUsage = usagePeriod === 'daily' ? usageFrequency : usageFrequency / 7;

      const monthlyDisposableCost = dailyUsage * disposableCost * 30;
      const monthlyReusableCost   = reusableCost / LIFESPAN_MONTHS;

      const monthlySavings = monthlyDisposableCost - monthlyReusableCost;
      const yearlySavings  = monthlySavings * 12;

      // "Lifetime" here = fixed 3 years (36 months)
      const periodsInThreeYears = usagePeriod === 'daily' ? (365 * 3) : (52 * 3);
      const totalDisposableCostThreeYears = disposableCost * usageFrequency * periodsInThreeYears;
      const lifetimeSavings = totalDisposableCostThreeYears - reusableCost;

      savings.breakeven = Math.max(0, breakeven);
      savings.yearly    = Math.max(0, yearlySavings);
      savings.lifetime  = Math.max(0, lifetimeSavings);
      savings.fiveYear  = savings.lifetime; // keep original property name working

      updateDisplay();
    }

    function updateDisplay() {
      // Breakeven
      if (elements.breakEvenValue) {
        if (savings.breakeven > 0) {
          const unit = state.usagePeriod === 'daily' ? 'days' : 'weeks';
          elements.breakEvenValue.textContent = `${Math.ceil(savings.breakeven)} ${unit}`;
        } else {
          elements.breakEvenValue.textContent = '--';
        }
      }

      // Savings values
      if (elements.yearlyValue)   elements.yearlyValue.textContent   = formatCurrency(savings.yearly);
      if (elements.lifetimeValue) elements.lifetimeValue.textContent = formatCurrency(savings.lifetime);

      // Share section visibility
      if (elements.shareSection) elements.shareSection.style.display = savings.yearly > 0 ? 'block' : 'none';

      updateChart();
    }

    function formatCurrency(amount) {
      try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
      } catch {
        return `$${(Math.round(amount * 100) / 100).toFixed(2)}`;
      }
    }

    function setupTabs() {
      const tabTriggers = $$('.tab-trigger');
      const tabContents = $$('.tab-content');
      if (!tabTriggers.length || !tabContents.length) return;

      tabTriggers.forEach(trigger => {
        trigger.addEventListener('click', () => {
          const tabName = trigger.dataset.tab;
          tabTriggers.forEach(t => t.classList.remove('active'));
          tabContents.forEach(c => c.classList.remove('active'));
          trigger.classList.add('active');
          const content = document.getElementById(`${tabName}-tab`);
          if (content && root.contains(content)) content.classList.add('active');
        });
      });
    }

    function generateChartData() {
      const data = [];
      const perDay = state.usagePeriod === 'daily' ? state.usageFrequency : state.usageFrequency / 7;
      const monthlyDisposable = perDay * state.disposableCost * 30;
      const monthlyReusable   = state.reusableCost / LIFESPAN_MONTHS;

      for (let month = 0; month <= 60; month += 6) {
        const cumulativeDisposable = monthlyDisposable * month;
        const cumulativeReusable   = monthlyReusable * month;
        data.push({
          month,
          disposable: cumulativeDisposable,
          reusable: cumulativeReusable,
          savings: Math.max(0, cumulativeDisposable - cumulativeReusable)
        });
      }
      return data;
    }

    function updateChart() {
      const svg = elements.costChart;
      if (!svg) return;

      const chartData = generateChartData();
      const maxValue = Math.max(...chartData.map(d => d.disposable), 0);

      svg.innerHTML = '';

      // Show hint if no data
      if (maxValue === 0) {
        svg.setAttribute('viewBox', '0 0 100 30');
        const msg = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        msg.setAttribute('x', '50');
        msg.setAttribute('y', '20');
        msg.setAttribute('text-anchor', 'middle');
        msg.setAttribute('font-size', '6');
        msg.textContent = 'Enter values to see chart';
        svg.appendChild(msg);
        return;
      }

      // Dimensions
      const width = 100, height = 100;
      const margin = { top: 5, right: 5, bottom: 15, left: 15 };
      const chartWidth  = width  - margin.left - margin.right;
      const chartHeight = height - margin.top  - margin.bottom;

      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

      // Grid + Y labels
      for (let i = 0; i <= 4; i++) {
        const ratio = i / 4;
        const y = margin.top + chartHeight - (ratio * chartHeight);

        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', margin.left);
        line.setAttribute('y1', y);
        line.setAttribute('x2', width - margin.right);
        line.setAttribute('y2', y);
        line.setAttribute('stroke', '#20232E');
        line.setAttribute('stroke-width', '0.2');
        line.setAttribute('opacity', '0.3');
        svg.appendChild(line);

        const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        label.setAttribute('x', '2');
        label.setAttribute('y', y + 1);
        label.setAttribute('font-size', '3');
        label.setAttribute('font-family', 'Montserrat, sans-serif');
        label.setAttribute('fill', 'rgba(32, 35, 46, 0.7)');
        const val = maxValue * ratio;
        label.textContent = val >= 1000 ? ('$' + (val / 1000).toFixed(0) + 'k') : formatCurrency(val);
        svg.appendChild(label);
      }

      // Build point strings
      const toPoints = (fn) => chartData.map((d, i) => {
        const x = margin.left + (i * (chartWidth / (chartData.length - 1)));
        const y = margin.top + chartHeight - (fn(d) / maxValue * chartHeight);
        return `${x},${y}`;
      }).join(' ');

      // Disposable line
      const disposableLine = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
      disposableLine.setAttribute('fill', 'none');
      disposableLine.setAttribute('stroke', '#C3996A');
      disposableLine.setAttribute('stroke-width', '0.8');
      disposableLine.setAttribute('points', toPoints(d => d.disposable));
      svg.appendChild(disposableLine);

      // Reusable line
      const reusableLine = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
      reusableLine.setAttribute('fill', 'none');
      reusableLine.setAttribute('stroke', '#004259');
      reusableLine.setAttribute('stroke-width', '0.8');
      reusableLine.setAttribute('points', toPoints(d => d.reusable));
      svg.appendChild(reusableLine);

      // X-axis labels
      chartData.forEach((d, i) => {
        const x = margin.left + (i * (chartWidth / (chartData.length - 1)));
        const y = height - margin.bottom + 4;

        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', x);
        text.setAttribute('y', y);
        text.setAttribute('font-size', '3');
        text.setAttribute('font-family', 'Montserrat, sans-serif');
        text.setAttribute('fill', 'rgba(32, 35, 46, 0.7)');
        text.setAttribute('text-anchor', 'middle');
        text.textContent = `${d.month}m`;
        svg.appendChild(text);
      });
    }

    // Social sharing
    function generateShareText() {
      return `💰 I could save ${formatCurrency(savings.yearly)} per year by switching to Fen X Custom earplugs! Calculate your savings:`;
    }

    function handleShare(e) {
      e.preventDefault?.();
      const platform = e.currentTarget?.dataset?.platform;
      const text = encodeURIComponent(generateShareText());
      const url  = encodeURIComponent(window.location.href);

      let shareLink = '';
      switch (platform) {
        case 'twitter':
        case 'x':
          shareLink = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
          break;
        case 'facebook':
          shareLink = `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`;
          break;
        case 'linkedin':
          shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${url}&summary=${text}`;
          break;
        default:
          return;
      }
      window.open(shareLink, '_blank', 'width=600,height=400');
    }

    // Copy to clipboard
    async function handleCopyToClipboard() {
      const text = `${generateShareText()} ${window.location.href}`;
      const copyText  = elements.copyBtn?.querySelector('.copy-text');
      const copyIcon  = elements.copyBtn?.querySelector('.copy-icon');
      const checkIcon = elements.copyBtn?.querySelector('.check-icon');

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.left = '-999999px';
          ta.style.top  = '-999999px';
          document.body.appendChild(ta);
          ta.focus(); ta.select();
          const ok = document.execCommand('copy');
          document.body.removeChild(ta);
          if (!ok) throw new Error('execCommand failed');
        }

        if (copyText)  copyText.textContent = 'Copied!';
        if (copyIcon)  copyIcon.style.display = 'none';
        if (checkIcon) checkIcon.style.display = 'block';
        if (elements.copyFeedback) elements.copyFeedback.style.display = 'block';

        setTimeout(() => {
          if (copyText)  copyText.textContent = 'Copy Link';
          if (copyIcon)  copyIcon.style.display = 'block';
          if (checkIcon) checkIcon.style.display = 'none';
          if (elements.copyFeedback) elements.copyFeedback.style.display = 'none';
        }, 2000);
      } catch (err) {
        console.error('[Calculator] Copy failed:', err);
        if (copyText)  copyText.textContent = 'Copied!';
        if (copyIcon)  copyIcon.style.display = 'none';
        if (checkIcon) checkIcon.style.display = 'block';
        setTimeout(() => {
          if (copyText)  copyText.textContent = 'Copy Link';
          if (copyIcon)  copyIcon.style.display = 'block';
          if (checkIcon) checkIcon.style.display = 'none';
        }, 2000);
      }
    }
  };

  // Optional: auto‑init any calculators present on first load (safe no‑op if Step B1 already initialized)
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-section-type="calculator"]').forEach(el => {
      if (typeof window.initCalculator === 'function') window.initCalculator(el.id);
    });
  });
})();
